import { useMemo, useState } from 'react';
import AppLayout from '../components/AppLayout';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { listChatSessions, getChatSession, getSavedMessages } from '../lib/api';
import type { SessionDetail, SessionSummary, SavedMessage } from '../types/session';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Calendar, Filter, Users, Tag, Clock, Search } from 'lucide-react';
import { getCoachProfile, getAllCoachProfiles } from '../lib/coachProfiles';
import { hasCoachImage, getCoachImage } from '../lib/coachImages';
import { useTakeawayAnalytics } from '../hooks/useTakeawayAnalytics';

const formatMinutes = (mins: number) => {
  if (mins <= 0) return '—';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m ? ` ${m}m` : ''}`;
};

export default function TakeawaysArchiveView() {
  const { getToken, isSignedIn } = useAuth();
  const [coachFilter, setCoachFilter] = useState<string>('');
  const [monthYear, setMonthYear] = useState<string>('');
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];
  const [themeSearch, setThemeSearch] = useState<string>('');

  // Build dropdown options for exactly six coaches (stable order)
  const coachOptions = useMemo(() => {
    const allow = ['rajesh', 'samuel', 'priya', 'meera', 'aisha', 'arjun'];
    const profiles = (typeof getAllCoachProfiles === 'function') ? getAllCoachProfiles() : [];
    const byId: Record<string, any> = {};
    profiles.forEach((p: any) => { byId[p.id] = p; });
    return allow.map(id => ({ id, name: byId[id]?.name || id.charAt(0).toUpperCase() + id.slice(1) }));
  }, []);

  const { data: sessions = [], isLoading } = useQuery<SessionSummary[]>({
    queryKey: ['sessions-archive'],
    enabled: isSignedIn,
    queryFn: async () => {
      const token = await getToken();
      if (!token) return [];
      return listChatSessions(token);
    }
  });

  const { data: allSavedMessages = [] } = useQuery<SavedMessage[]>({
    queryKey: ['all-saved-messages'],
    enabled: isSignedIn,
    queryFn: async () => {
      const token = await getToken();
      if (!token) return [];
      return getSavedMessages(token);
    }
  });

  // Filter to sessions that actually have a summary (mom_data) OR saved nuggets
  const summarizedSessions = useMemo(() => {
    const sessionsWithNuggetsIds = new Set(
      allSavedMessages
        .filter(m => m.sessionId)
        .map(m => m.sessionId)
    );

    const filtered = (sessions || []).filter(s => {
      // More robust MoM data validation with v2-slim support
      let hasMom = false;
      if (s.mom_data) {
        if (typeof s.mom_data === 'string') {
          if (s.mom_data.trim() !== '' && s.mom_data !== '{}' && s.mom_data !== 'null') {
            try {
              const parsed = JSON.parse(s.mom_data);
              hasMom = !!(parsed && typeof parsed === 'object' && (
                parsed.keyTakeaways || parsed.title || parsed.summary || parsed.observationalSummary || parsed.goldenInsights
              ));
            } catch {
              hasMom = false;
            }
          }
        } else if (typeof s.mom_data === 'object' && s.mom_data !== null) {
          hasMom = !!(Object.keys(s.mom_data).length > 0 && (
            s.mom_data.keyTakeaways || s.mom_data.title || s.mom_data.summary || s.mom_data.observationalSummary || s.mom_data.goldenInsights
          ));
        }
      }

      const hasNuggets = sessionsWithNuggetsIds.has(s.id);
      return hasMom || hasNuggets;
    });

    console.log(`[MoM-FILTER-DEBUG] Total sessions: ${(sessions || []).length}, With MoM or Nuggets: ${filtered.length}`);
    return filtered;
  }, [sessions, allSavedMessages]);

  // Fetch details for sessions that pass filters to get saved messages
  const [detailsById, setDetailsById] = useState<Record<string, SessionDetail>>({});

  useQuery({
    queryKey: ['session-details-archive', summarizedSessions.map(s => s.id)],
    enabled: isSignedIn && summarizedSessions.length > 0,
    queryFn: async () => {
      const token = await getToken();
      if (!token) return {} as Record<string, SessionDetail>;
      const results: Record<string, SessionDetail> = {};
      for (const s of summarizedSessions) {
        try {
          const d = await getChatSession(s.id, token);
          if (d) results[s.id] = d;
        } catch (e) {
          // ignore failures to keep archive robust
        }
      }
      setDetailsById(results);
      return results;
    }
  });

  // Analytics and tags from summaries
  const analytics = useTakeawayAnalytics(summarizedSessions);

  const filtered = useMemo(() => {
    return summarizedSessions.filter(s => {
      const coachOk = coachFilter
        ? (s.coachTypes || []).map(x => String(x).toLowerCase()).includes(coachFilter.toLowerCase().trim())
        : true;

      // Month-Year filter
      const date = new Date(s.updated_at || s.created_at);
      const sessionMonthYear = date.toISOString().substring(0, 7); // YYYY-MM format
      const monthYearOk = monthYear ? sessionMonthYear === monthYear : true;

      // Combined theme and content search
      const momText = JSON.stringify(s.mom_data || {}).toLowerCase();
      const sessionNuggets = allSavedMessages
        .filter(m => m.sessionId === s.id)
        .map(m => m.messageContent.toLowerCase())
        .join(' ');

      const tags: string[] = analytics[s.id]?.themes || [];
      const themeSearchLower = themeSearch.toLowerCase();

      const themeOk = !themeSearch ||
        momText.includes(themeSearchLower) ||
        sessionNuggets.includes(themeSearchLower) ||
        tags.some(t => t.toLowerCase().includes(themeSearchLower));

      return coachOk && monthYearOk && themeOk;
    });
  }, [summarizedSessions, coachFilter, monthYear, themeSearch, analytics, allSavedMessages]);

  const manualNuggets = useMemo(() => {
    return allSavedMessages.filter(m => {
      if (m.sessionId) return false;
      if (!themeSearch) return true;
      return m.messageContent.toLowerCase().includes(themeSearch.toLowerCase()) ||
        m.coachType.toLowerCase().includes(themeSearch.toLowerCase());
    });
  }, [allSavedMessages, themeSearch]);

  return (
    <AppLayout currentPage="insights-hub" >
      <div className="min-h-screen px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm border border-[#4C62E3]/20">
                <Calendar className="h-6 w-6 text-[#4C62E3]" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Key Takeaways Archive</h1>
                <p className="text-base text-slate-600 leading-relaxed">Review past session summaries, golden nuggets, and themes</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="flex items-center gap-2 rounded-2xl border border-[#D5D5D5] bg-white px-3 py-2">
                <Search className="h-4 w-4 text-slate-500" />
                <input
                  type="text"
                  value={themeSearch}
                  onChange={(e) => setThemeSearch(e.target.value)}
                  placeholder="Search by theme or content..."
                  className="w-full bg-transparent text-sm outline-none"
                />
              </div>
              <div className="flex items-center gap-2 rounded-2xl border border-[#D5D5D5] bg-white px-3 py-2">
                <Users className="h-4 w-4 text-slate-500" />
                <select
                  aria-label="Filter by coach"
                  value={coachFilter}
                  onChange={(e) => setCoachFilter(e.target.value)}
                  className="w-full bg-transparent text-sm outline-none cursor-pointer"
                  title="Filter by coach"
                >
                  <option value="">Select coach</option>
                  {coachOptions.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 rounded-2xl border border-[#D5D5D5] bg-white px-3 py-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                <div className="flex gap-2 w-full">
                  <select
                    value={monthYear.split('-')[1] || ''}
                    onChange={(e) => {
                      const year = monthYear.split('-')[0] || new Date().getFullYear().toString();
                      setMonthYear(e.target.value ? `${year}-${e.target.value}` : '');
                    }}
                    className="w-full bg-transparent text-sm outline-none cursor-pointer"
                    aria-label="Select month"
                    title="Select month"
                  >
                    <option value="">Month</option>
                    {months.map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                  <select
                    value={monthYear.split('-')[0] || ''}
                    onChange={(e) => {
                      const month = monthYear.split('-')[1] || '';
                      setMonthYear(e.target.value ? `${e.target.value}-${month}` : '');
                    }}
                    className="w-full bg-transparent text-sm outline-none cursor-pointer"
                    aria-label="Select year"
                    title="Select year"
                  >
                    <option value="">Year</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Archive list */}
          <div className="grid gap-6">
            {isLoading && (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center">Loading takeaways…</div>
            )}
            {!isLoading && filtered.length === 0 && (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center space-y-4">
                <p className="text-slate-600">No MoMs found for the selected filters.</p>
                <div className="text-xs text-slate-500">
                  <p>Debug Info: {(sessions || []).length} total sessions, {summarizedSessions.length} with MoM data</p>
                  {process.env.NODE_ENV === 'development' && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-blue-600 hover:text-blue-800">View Debug Details</summary>
                      <div className="mt-2 text-left bg-slate-100 p-2 rounded text-xs font-mono">
                        <p>Sessions: {JSON.stringify((sessions || []).slice(0, 2).map(s => ({
                          id: s.id,
                          mom_data: typeof s.mom_data === 'string' ? s.mom_data.substring(0, 50) + '...' : s.mom_data
                        })), null, 2)}</p>
                      </div>
                    </details>
                  )}
                </div>
              </div>
            )}

            {filtered.map((s) => {
              const mom: any = s.mom_data || {};
              const nuggets = allSavedMessages
                .filter(m => m.sessionId === s.id)
                .map(m => m.messageContent)
                .filter(Boolean);

              // Get MoM generation date or fall back to session date
              const momGeneratedAt = mom?.meetingMeta?.generatedAt;
              const displayDate = momGeneratedAt ? new Date(momGeneratedAt) : new Date(s.updated_at || s.created_at);

              // Format as DD-MM-YYYY
              const formatDate = (date: Date) => {
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
              };

              // Approx duration
              const created = new Date(s.created_at);
              const updated = new Date(s.updated_at);
              const mins = Math.max(0, Math.round((updated.getTime() - created.getTime()) / 60000));

              const coachIcons = (s.coachTypes || []).slice(0, 3).map((cid: string) => getCoachProfile(cid));
              const visibleCoaches = coachIcons.filter(Boolean) as any[];

              // Build discussion points string ~100 words (v2-slim aware)
              let words = '';
              if (mom?.version === 'mom.v2slim') {
                const text = typeof mom?.observationalSummary === 'string' ? mom.observationalSummary : '';
                words = text.split(/\s+/).slice(0, 100).join(' ');
              } else {
                const points = Array.isArray(mom?.keyTakeaways) ? mom.keyTakeaways.join(' ') : '';
                words = points.split(/\s+/).slice(0, 100).join(' ');
              }
              const tags = analytics[s.id]?.themes || [];

              return (
                <Card key={s.id} className="bg-white rounded-3xl border border-[#D5D5D5] shadow-sm">
                  <CardContent className="p-6 md:p-8 space-y-4">
                    {/* Header */}
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-slate-500" />
                        <span className="text-sm text-slate-600">
                          {momGeneratedAt ? 'MoM generated on ' : 'Session from '}
                          {formatDate(displayDate)}
                        </span>
                        <span className="mx-2 text-slate-300">•</span>
                        <Clock className="h-4 w-4 text-slate-500" />
                        <span className="text-sm text-slate-600">{formatMinutes(mins)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {coachIcons.map((coach) => coach && (
                          <div key={coach.id} className="h-8 w-8 rounded-full overflow-hidden border-2 border-white shadow-sm bg-white" title={coach.name} aria-label={coach.name}>
                            {hasCoachImage(coach.id) ? (
                              <img src={getCoachImage(coach.id)} alt={coach.name} title={coach.name} className="h-full w-full object-cover" />
                            ) : (
                              <div className={`w-full h-full bg-gradient-to-br ${coach.color} flex items-center justify-center text-white text-[10px] font-display font-bold`} title={coach.name} aria-label={coach.name} role="img">
                                {coach.name.charAt(0)}
                              </div>
                            )}
                          </div>
                        ))}
                        {visibleCoaches[0] && (
                          <span className="hidden md:inline text-xs text-slate-600 ml-2">{visibleCoaches[0].name}</span>
                        )}
                        {visibleCoaches.length > 1 && (
                          <span className="hidden md:inline text-xs text-slate-400">+{visibleCoaches.length - 1}</span>
                        )}
                      </div>
                    </div>

                    {/* Discussion Points */}
                    <div>
                      <h3 className="font-display text-lg font-bold text-slate-900 mb-2">Discussion Points</h3>
                      <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{words || '—'}</p>
                    </div>

                    {/* Golden Nuggets */}
                    <div>
                      <h3 className="font-display text-lg font-bold text-slate-900 mb-2">Golden Nuggets</h3>
                      {nuggets.length === 0 ? (
                        <p className="text-sm text-slate-600">No saved nuggets for this session.</p>
                      ) : (
                        <ul className="space-y-2">
                          {nuggets.map((t, idx) => (
                            <li key={idx} className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-sm text-slate-800">{t}</li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Themes / Tags */}
                    <div>
                      <h3 className="font-display text-lg font-bold text-slate-900 mb-2">Themes</h3>
                      <div className="flex flex-wrap gap-2">
                        {(tags.length ? tags : ['Strategy']).map((t: string) => (
                          <span key={t} className="inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs text-slate-700">
                            <Tag className="h-3 w-3 text-slate-500" />
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Manual Nuggets section */}
          {manualNuggets.length > 0 && (
            <div className="mt-12 space-y-6">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm border border-amber-200">
                  <Tag className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Personal Insights</h2>
                  <p className="text-base text-slate-600 leading-relaxed">Saved nuggets and manual goals not linked to a session</p>
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {manualNuggets.map((nugget) => (
                  <Card key={nugget.id} className="bg-white rounded-3xl border border-[#D5D5D5] shadow-sm hover:shadow-md transition-all duration-300">
                    <CardContent className="p-6 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">
                          {new Date(nugget.createdAt).toLocaleDateString()}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                          {nugget.coachType}
                        </span>
                      </div>
                      <p className="text-sm text-slate-800 leading-relaxed font-body">{nugget.messageContent}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
